// ============================================================
//  O365 – Standalone API (Core-Service Endpoints)
//  Keine Abhaengigkeit zum CRM-Modul.
// ============================================================

import api from '../api/client';

// ── Types ──────────────────────────────────────────────────────────────────

export interface UserIntegration {
  provider: string;
  connected: boolean;
  connectedAt?: string;
}

export interface M365MailFolder {
  id: string;
  displayName: string;
  wellKnownName?: string;
  totalItemCount: number;
  unreadItemCount: number;
  childFolderCount?: number;
}

export interface M365Email {
  id: string;
  subject: string;
  from?: {
    emailAddress?: { name: string; address: string };
  };
  receivedDateTime: string;
  isRead: boolean;
  hasAttachments: boolean;
  bodyPreview: string;
  webLink: string;
  body?: { content: string; contentType: string };
  toRecipients?: Array<{ emailAddress?: { name: string; address: string } }>;
}

export interface M365CalendarEvent {
  id: string;
  subject: string;
  start: { dateTime: string; timeZone: string };
  end: { dateTime: string; timeZone: string };
  location?: { displayName?: string };
  organizer?: { emailAddress?: { name: string; address: string } };
  attendees?: Array<{
    emailAddress?: { name: string; address: string };
    status?: { response: string };
  }>;
  isOnlineMeeting?: boolean;
  onlineMeetingUrl?: string;
  webLink?: string;
}

export interface M365TaskFlat {
  id: string;
  title: string;
  status: string;
  dueDateTime?: { dateTime: string; timeZone: string };
  importance?: string;
  body?: { content: string; contentType: string };
  bodyContent?: string;
  listId: string;
  listName: string;
}

export interface M365Contact {
  id: string;
  displayName: string;
  emailAddresses: Array<{ address: string; name?: string }>;
  mobilePhone?: string;
  businessPhones: string[];
  jobTitle?: string;
  companyName?: string;
}

// ── API Functions ──────────────────────────────────────────────────────────

// Graph-Proxy läuft im CRM-Service (/api/v1/crm/office365/...)
const CRM = '/crm/office365';

export const o365Api = {
  /** Liste der Integrationen (M365 connected?) — Core-Service */
  getIntegrations: () =>
    api
      .get<{ success: boolean; data: UserIntegration[] }>('/users/me/integrations')
      .then((r) => r.data),

  /** Mailordner auflisten */
  getMailFolders: () =>
    api
      .get<{ success: boolean; data: M365MailFolder[] }>(`${CRM}/folders`)
      .then((r) => r.data),

  /** Unterordner eines Mailordners laden */
  getChildMailFolders: (folderId: string) =>
    api
      .get<{ success: boolean; data: M365MailFolder[] }>(`${CRM}/folders/${folderId}/childFolders`)
      .then((r) => r.data),

  /** Nachrichten in einem Ordner laden */
  getMailsInFolder: (folderId: string, days: number) =>
    api
      .get<{ success: boolean; data: M365Email[] }>(
        `${CRM}/folders/${folderId}/messages`,
        { params: { days } },
      )
      .then((r) => r.data),

  /** Kalendertermine in einem Zeitbereich laden */
  getCalendarRange: (startDate: string, endDate: string) =>
    api
      .get<{ success: boolean; data: M365CalendarEvent[] }>(
        `${CRM}/calendar/range`,
        { params: { startDate, endDate } },
      )
      .then((r) => r.data),

  /** Alle offenen Aufgaben (flach) */
  getTasksFlat: () =>
    api
      .get<{ success: boolean; data: M365TaskFlat[] }>(`${CRM}/tasks/flat`)
      .then((r) => r.data),

  /** Aufgabe als erledigt markieren */
  completeTask: (listId: string, taskId: string) =>
    api
      .patch<{ success: boolean }>(`${CRM}/tasks/${listId}/${taskId}/complete`, {})
      .then((r) => r.data),

  /** Outlook-Kontakte laden */
  getContacts: () =>
    api
      .get<{ success: boolean; data: M365Contact[] }>(`${CRM}/contacts`)
      .then((r) => r.data),
};
